import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  IoCheckmarkCircleOutline,
  IoCheckmark,
  IoCloudDownloadOutline,
  IoCopyOutline,
  IoOpenOutline,
  IoRefreshOutline,
  IoSearchOutline,
  IoShareSocialOutline,
} from "react-icons/io5";
import Menu from "components/common/Menu";
import { SideBarTabs } from "constants/tabs";
import { useTabActive } from "hook/useTabActive";
import { Paths } from "router/Paths";
import {
  DistributionCar,
  DistributionPlatform,
  GetDistributionCars,
  ListingDistribution,
  PrepareDistribution,
  UpdateDistribution,
} from "api/services/distributions";

const platforms: DistributionPlatform[] = ["Facebook", "Jiji", "Gari"];
const platformColors: Record<DistributionPlatform, string> = {
  Facebook: "#1877F2",
  Jiji: "#36A420",
  Gari: "#F59E0B",
};
const JIJI_EXTENSION_SOURCE = "bosscar-jiji-extension";
const JIJI_PAGE_SOURCE = "bosscar-dashboard";

type JijiProgress = {
  distributionId?: string;
  stage?: string;
  message?: string;
  publishedUrl?: string;
};

const makeName = (car: DistributionCar) =>
  typeof car.make === "object" ? car.make?.name : car.make;

const formatPrice = (value: string | number) =>
  `${Number(value || 0).toLocaleString("en-US")} Birr`;

const distributionStatusLabel = (record: ListingDistribution) => {
  if (record.platform !== "Gari" && record.platform !== "Facebook") return record.status;
  if (record.status === "Ready") return "Queued";
  if (record.status === "Processing") return "Posting";
  if (record.status === "NeedsReview") return "Needs attention";
  return record.status;
};

const platformUnavailable = (car: DistributionCar, platform: DistributionPlatform) => {
  const blocked = car.blockedPlatforms?.[platform];
  if (blocked) return blocked;
  const record = car.distributions.find((item) => item.platform === platform);
  if (record?.status === "Published") return `Already published to ${platform}`;
  if ((platform === "Facebook" || platform === "Gari") && record && ["Ready", "Processing"].includes(record.status)) {
    return `${platform}: ${distributionStatusLabel(record)}`;
  }
  return "";
};

const copyText = async (value: string) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  textarea.remove();
};

const downloadImages = async (images: string[], title: string) => {
  const safeTitle = title.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "");
  for (let index = 0; index < images.length; index += 1) {
    try {
      const response = await fetch(images[index]);
      if (!response.ok) throw new Error("Image download failed");
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = `${safeTitle || "bosscar"}-${index + 1}.${blob.type.includes("png") ? "png" : "jpg"}`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(images[index], "_blank", "noopener,noreferrer");
    }
  }
};

const queueJijiPackage = (record: ListingDistribution, attemptToken: string) =>
  new Promise<boolean>((resolve) => {
    let settled = false;
    const finish = (value: boolean) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", listener);
      window.clearTimeout(timeout);
      resolve(value);
    };
    const listener = (event: MessageEvent) => {
      if (event.source !== window || event.origin !== window.location.origin) return;
      if (event.data?.source !== JIJI_EXTENSION_SOURCE) return;
      if (event.data?.distributionId !== record._id) return;
      if (event.data?.type === "BOSSCAR_JIJI_STORED") finish(true);
      if (event.data?.type === "BOSSCAR_JIJI_ERROR") finish(false);
    };
    const timeout = window.setTimeout(() => finish(false), 1800);
    window.addEventListener("message", listener);
    window.postMessage(
      {
        source: JIJI_PAGE_SOURCE,
        type: "BOSSCAR_JIJI_PREPARE",
        payload: {
          distributionId: record._id,
          attemptToken,
          carId: record.car,
          title: record.title,
          description: record.description,
          phone: record.phone,
          images: record.images,
          snapshot: record.snapshot || {},
          autoSubmit: true,
        },
      },
      window.location.origin
    );
  });

function DistributionPage() {
  useTabActive(SideBarTabs.Distribution.name);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pinnedCarId = searchParams.get("car") || "";
  const [cars, setCars] = useState<DistributionCar[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState("");
  const [postingAllCarId, setPostingAllCarId] = useState("");
  const [selectedPlatforms, setSelectedPlatforms] = useState<Record<string, DistributionPlatform[]>>({});
  const [postResults, setPostResults] = useState<Record<string, string>>({});
  const [feedback, setFeedback] = useState("");
  const [selected, setSelected] = useState<ListingDistribution | null>(null);
  const [publishedUrl, setPublishedUrl] = useState("");
  const [jijiHelperReady, setJijiHelperReady] = useState(false);
  const [showJijiSetup, setShowJijiSetup] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    const response = await GetDistributionCars({
      search: search || undefined,
      limit: 100,
    });
    if (!silent) setLoading(false);
    if (response.status !== 200) {
      setFeedback(response.message);
      return;
    }
    setCars(response.cars);
    if (!silent) setFeedback("");
  }, [search]);

  useEffect(() => {
    const hasActivePublisherJob = cars.some((car) =>
      car.distributions.some(
        (record) =>
          (record.platform === "Gari" || record.platform === "Facebook") &&
          ["Ready", "Processing", "NeedsReview"].includes(record.status)
      )
    );
    if (!hasActivePublisherJob) return undefined;
    const timer = window.setInterval(() => void load(true), 5000);
    return () => window.clearInterval(timer);
  }, [cars, load]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 250);
    return () => window.clearTimeout(timer);
  }, [load]);

  const orderedCars = useMemo(() => {
    if (!pinnedCarId) return cars;
    return [...cars].sort((a, b) =>
      String(a._id) === pinnedCarId ? -1 : String(b._id) === pinnedCarId ? 1 : 0
    );
  }, [cars, pinnedCarId]);

  const mergeDistribution = useCallback((record: ListingDistribution) => {
    setCars((current) =>
      current.map((car) =>
        String(car._id) !== String(record.car)
          ? car
          : {
              ...car,
              distributions: [
                record,
                ...car.distributions.filter((item) => item._id !== record._id),
              ],
            }
      )
    );
  }, []);

  useEffect(() => {
    const listener = (event: MessageEvent) => {
      if (event.source !== window || event.origin !== window.location.origin) return;
      if (event.data?.source !== JIJI_EXTENSION_SOURCE) return;
      if (event.data?.type === "BOSSCAR_JIJI_READY") {
        setJijiHelperReady(true);
        return;
      }
      if (event.data?.type !== "BOSSCAR_JIJI_PROGRESS") return;

      const progress = (event.data?.payload || {}) as JijiProgress;
      if (progress.message) setFeedback(progress.message);
      if (progress.stage !== "complete" || !progress.distributionId) return;

      void UpdateDistribution(progress.distributionId, {
        status: "Published",
        publishedUrl: progress.publishedUrl || "",
      }).then((response) => {
        if (response.status === 200 && response.distribution) {
          mergeDistribution(response.distribution);
          setFeedback("The Jiji ad was submitted successfully.");
        }
      });
    };

    window.addEventListener("message", listener);
    window.postMessage(
      { source: JIJI_PAGE_SOURCE, type: "BOSSCAR_JIJI_PING" },
      window.location.origin
    );
    return () => window.removeEventListener("message", listener);
  }, [mergeDistribution]);

  const prepareAndOpen = async (
    car: DistributionCar,
    platform: DistributionPlatform,
    options?: {
      popup?: Window | null;
      manageBusy?: boolean;
      suppressFeedback?: boolean;
    }
  ) => {
    const key = `${car._id}:${platform}`;
    const manageBusy = options?.manageBusy !== false;
    const reportFeedback = options?.suppressFeedback !== true;
    if (manageBusy) setBusyKey(key);
    if (reportFeedback) setFeedback("");
    const usesPublisher = platform === "Gari" || platform === "Facebook";
    const popup = usesPublisher
      ? null
      : options && Object.prototype.hasOwnProperty.call(options, "popup")
      ? options.popup || null
      : window.open("about:blank", "_blank");
    let response: Awaited<ReturnType<typeof PrepareDistribution>>;
    try {
      response = await PrepareDistribution(String(car._id), platform);
    } catch (error) {
      popup?.close();
      if (manageBusy) setBusyKey("");
      const message =
        error instanceof Error
          ? error.message
          : `Could not prepare the ${platform} listing.`;
      if (reportFeedback) setFeedback(message);
      return { success: false, message };
    }
    if (response.status !== 200 || !response.distribution) {
      popup?.close();
      if (manageBusy) setBusyKey("");
      if (reportFeedback) setFeedback(response.message);
      return { success: false, message: response.message };
    }

    const record = response.distribution;
    if (usesPublisher) {
      mergeDistribution(record);
      if (manageBusy) setBusyKey("");
      const message = `Sent to the ${platform} publisher. ${platform} will open and fill the ad now.`;
      if (reportFeedback) setFeedback(message);
      return { success: true, message };
    }

    if (platform === "Jiji") {
      const attemptToken = window.crypto.randomUUID();
      const queued = await queueJijiPackage(record, attemptToken);
      if (!queued) {
        popup?.close();
        if (manageBusy) setBusyKey("");
        setSelected(record);
        setPublishedUrl(record.publishedUrl || "");
        setShowJijiSetup(true);
        const message = "Install the BossCar Jiji helper once to enable one-button posting.";
        if (reportFeedback) setFeedback(message);
        return { success: false, message };
      }

      const jijiUrl = new URL(record.targetUrl);
      jijiUrl.hash = new URLSearchParams({ "bosscar-attempt": attemptToken }).toString();
      if (popup) {
        popup.opener = null;
        popup.location.href = jijiUrl.href;
      } else {
        window.open(jijiUrl.href, "_blank", "noopener,noreferrer");
      }
      const opened = await UpdateDistribution(record._id, { status: "Opened" });
      mergeDistribution(opened.status === 200 ? opened.distribution : record);
      if (manageBusy) setBusyKey("");
      const message = "Jiji publishing started. Keep the Jiji tab open until it finishes.";
      if (reportFeedback) setFeedback(message);
      return { success: true, message };
    }

    try {
      await copyText(record.description);
      setFeedback(`${platform} copy is ready and has been copied.`);
    } catch {
      setFeedback(`${platform} copy is ready. Use Copy text in the review panel.`);
    }

    if (popup) {
      popup.opener = null;
      popup.location.href = record.targetUrl;
    } else {
      window.open(record.targetUrl, "_blank", "noopener,noreferrer");
    }

    const opened = await UpdateDistribution(record._id, { status: "Opened" });
    const current = opened.status === 200 ? opened.distribution : record;
    mergeDistribution(current);
    setSelected(current);
    setPublishedUrl(current.publishedUrl || "");
    if (manageBusy) setBusyKey("");
    return { success: true, message: `${platform} publishing started.` };
  };

  const postToSelectedPlatforms = async (car: DistributionCar) => {
    if (postingAllCarId || busyKey) return;
    const carId = String(car._id);
    const actionablePlatforms = platforms.filter((platform) =>
      selectedPlatforms[carId]?.includes(platform) && !platformUnavailable(car, platform)
    );

    if (actionablePlatforms.length === 0) {
      const message = "Select a platform that is not already published or queued.";
      setPostResults((current) => ({ ...current, [carId]: message }));
      setFeedback(message);
      return;
    }

    const jijiPopup = actionablePlatforms.includes("Jiji")
      ? window.open("about:blank", "_blank")
      : null;
    const results: Array<{
      platform: DistributionPlatform;
      success: boolean;
      message: string;
    }> = [];

    setPostingAllCarId(carId);
    const preparing = `Preparing ${actionablePlatforms.join(", ")}...`;
    setPostResults((current) => ({ ...current, [carId]: preparing }));
    setFeedback(preparing);

    try {
      for (const platform of actionablePlatforms) {
        try {
          const result = await prepareAndOpen(car, platform, {
            popup: platform === "Jiji" ? jijiPopup : null,
            manageBusy: false,
            suppressFeedback: true,
          });
          results.push({ platform, ...result });
        } catch (error) {
          results.push({
            platform,
            success: false,
            message:
              error instanceof Error
                ? error.message
                : `Could not start ${platform} publishing.`,
          });
        }
      }
    } finally {
      setPostingAllCarId("");
    }

    const started = results.filter((result) => result.success).map((result) => result.platform);
    setSelectedPlatforms((current) => ({
      ...current, [carId]: (current[carId] || []).filter((platform) => !started.includes(platform)),
    }));
    const failed = results.filter((result) => !result.success);
    const summary = started.length ? `Started: ${started.join(", ")}.` : "";
    const failures = failed.length
      ? ` Needs attention: ${failed.map((result) => `${result.platform} (${result.message})`).join("; ")}.`
      : "";
    const message = `${summary}${failures}`.trim();
    setPostResults((current) => ({ ...current, [carId]: message }));
    setFeedback(message);
  };

  const markPublished = async () => {
    if (!selected) return;
    setBusyKey(selected._id);
    const response = await UpdateDistribution(selected._id, {
      status: "Published",
      publishedUrl,
    });
    setBusyKey("");
    if (response.status !== 200) {
      setFeedback(response.message);
      return;
    }
    mergeDistribution(response.distribution);
    setSelected(response.distribution);
    setFeedback(`${response.distribution.platform} marked as published.`);
  };

  return (
    <div className={ClassName.page}>
      <Menu />
      <header className={ClassName.header}>
        <div>
          <span className={ClassName.eyebrow}>BOSSCAR DISTRIBUTION</span>
          <h1 className={ClassName.title}>Post cars across marketplaces</h1>
          <p className={ClassName.subtitle}>
            Prepare approved new and inspected cars once, then publish them quickly with consistent details.
          </p>
        </div>
        <button type="button" className={ClassName.refresh} onClick={() => void load()}>
          <IoRefreshOutline size={18} /> Refresh
        </button>
      </header>

      <main className={ClassName.main}>
        <section className={ClassName.guide}>
          <div className={ClassName.guideIcon}><IoShareSocialOutline size={22} /></div>
          <div>
            <strong>Fast, controlled publishing</strong>
            <p>Select platforms, then post. Details and photos are filled automatically.</p>
            <div className="mt-2 flex flex-wrap items-center gap-4 text-xs" aria-label="Platform color key">
              {platforms.map((platform) => <span key={platform} className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: platformColors[platform] }} aria-hidden="true" />
                {platform}
              </span>)}
            </div>
            <button
              type="button"
              className={jijiHelperReady ? ClassName.helperReady : ClassName.helperSetup}
              onClick={() => setShowJijiSetup(true)}
            >
              {jijiHelperReady ? "Jiji helper ready" : "Enable one-button Jiji posting"}
            </button>
          </div>
        </section>

        <div className={ClassName.searchRow}>
          <IoSearchOutline size={20} className="text-slate-400" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search make, model, year, tag, fuel or price"
          />
        </div>

        {feedback ? <div className={ClassName.feedback}>{feedback}</div> : null}

        {loading ? (
          <div className={ClassName.empty}>Loading eligible cars...</div>
        ) : orderedCars.length === 0 ? (
          <div className={ClassName.empty}>
            <strong>No eligible cars found.</strong>
            <span>Only active, non-expired cars appear here.</span>
          </div>
        ) : (
          <section className={ClassName.grid}>
            {orderedCars.map((car) => (
              <article
                key={String(car._id)}
                className={`${ClassName.card} ${String(car._id) === pinnedCarId ? ClassName.pinned : ""}`}
              >
                <button
                  type="button"
                  className={ClassName.carPreview}
                  onClick={() => navigate(Paths.car.replace(":id", String(car._id)))}
                >
                  <img src={car.images?.[0]} alt={`${makeName(car)} ${car.model}`} />
                  <div className="min-w-0 flex-1 text-left">
                    <div className={ClassName.badges}>
                      <span>{String(car.usageType).toLowerCase() === "new" ? "New car" : car.inspectionStatus === "Passed" ? "BossCar inspected" : "Used"}</span>
                      {car.tag ? <span>{car.tag}</span> : null}
                    </div>
                    <h2>{car.year} {makeName(car)} {car.model}</h2>
                    <strong>{formatPrice(car.price)}</strong>
                    <p>{[car.fuelType, car.transmissionType, car.color].filter(Boolean).join(" · ")}</p>
                  </div>
                </button>

                <div className={ClassName.platforms}>
                  <div className="flex items-center" role="group" aria-label={`Posting platforms for ${car.tag || car.model}`}>
                  {platforms.map((platform) => {
                    const carId = String(car._id);
                    const unavailable = platformUnavailable(car, platform);
                    const checked = !unavailable && Boolean(selectedPlatforms[carId]?.includes(platform));
                    const disabled = Boolean(unavailable || busyKey || postingAllCarId);
                    return (
                      <label key={platform} className={disabled ? ClassName.checkboxDisabled : ClassName.checkboxLabel}
                        title={unavailable || `Post to ${platform}`}>
                        <input type="checkbox" className="peer sr-only" checked={checked} disabled={disabled}
                          aria-label={`Post to ${platform}${unavailable ? `: ${unavailable}` : ""}`}
                          onChange={(event) => {
                            const nextChecked = event.target.checked;
                            setSelectedPlatforms((current) => ({ ...current,
                              [carId]: nextChecked ? [...(current[carId] || []).filter((item) => item !== platform), platform]
                                : (current[carId] || []).filter((item) => item !== platform),
                            }));
                          }} />
                        <span className={ClassName.checkboxBox} aria-hidden="true" style={{
                          backgroundColor: checked ? platformColors[platform] : "#FFFFFF",
                          borderColor: checked ? platformColors[platform] : "#D1D5DB",
                        }}>{checked ? <IoCheckmark size={17} color="#FFFFFF" strokeWidth={12} /> : null}</span>
                      </label>
                    );
                  })}
                  </div>
                  <button type="button" className={ClassName.postButton}
                    disabled={Boolean(postingAllCarId || busyKey) || !platforms.some((platform) =>
                      selectedPlatforms[String(car._id)]?.includes(platform) && !platformUnavailable(car, platform))}
                    onClick={() => void postToSelectedPlatforms(car)}>
                    {postingAllCarId === String(car._id) ? "Posting..." : "Post"}
                  </button>
                </div>

                {postResults[String(car._id)] ? (
                  <div className={ClassName.postResult} role="status">
                    {postResults[String(car._id)]}
                  </div>
                ) : null}
                {car.distributions.length ? <details className={ClassName.statusLine}>
                  <summary className="cursor-pointer text-xs font-medium text-slate-500">Posting status</summary>
                  {platforms.flatMap((platform) => car.distributions.filter((item) => item.platform === platform)).map((item) => (
                        <div key={item._id} className="mt-2">
                          <button type="button" className="font-semibold underline underline-offset-2"
                            onClick={() => { setSelected(item); setPublishedUrl(item.publishedUrl || ""); }}>
                          {item.platform}: {distributionStatusLabel(item)}
                          </button>
                          {item.platform === "Facebook" &&
                          item.status === "Failed" &&
                          item.lastError?.includes("waitForFunction")
                            ? " - Sign in to Facebook in the publisher Chrome window, then retry."
                            : item.lastError
                            ? ` - ${item.lastError.slice(0, 180)}`
                            : item.progressMessage
                            ? ` - ${item.progressMessage}`
                            : ""}
                        </div>
                      ))}
                </details> : null}
              </article>
            ))}
          </section>
        )}
      </main>

      {selected ? (
        <div className={ClassName.modalBackdrop} role="presentation" onMouseDown={() => setSelected(null)}>
          <section className={ClassName.modal} role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
            <div className={ClassName.modalHeader}>
              <div>
                <span className={ClassName.eyebrow}>{selected.platform.toUpperCase()} PACKAGE</span>
                <h2>Review and finish publishing</h2>
              </div>
              <button type="button" onClick={() => setSelected(null)}>Close</button>
            </div>
            <label className={ClassName.copyField}>
              <span>Title</span>
              <div><input readOnly value={selected.title} /><button type="button" onClick={() => void copyText(selected.title)}><IoCopyOutline /> Copy</button></div>
            </label>
            <label className={ClassName.copyField}>
              <span>Listing text</span>
              <textarea readOnly value={selected.description} rows={10} />
              <button type="button" className={ClassName.copyButton} onClick={() => void copyText(selected.description)}><IoCopyOutline /> Copy listing text</button>
            </label>
            <div>
              <div className={ClassName.photoHeader}>
                <span className={ClassName.fieldLabel}>Photos ({selected.images.length})</span>
                <button type="button" onClick={() => void downloadImages(selected.images, selected.title)}><IoCloudDownloadOutline /> Download photos</button>
              </div>
              <div className={ClassName.photos}>
                {selected.images.map((image, index) => (
                  <a href={image} target="_blank" rel="noreferrer" key={`${image}-${index}`} title="Open full photo">
                    <img src={image} alt={`Car ${index + 1}`} />
                    <span>{index + 1}</span>
                  </a>
                ))}
              </div>
            </div>
            <a className={ClassName.openTarget} href={selected.targetUrl} target="_blank" rel="noreferrer"><IoOpenOutline /> Open {selected.platform} again</a>
            <div className={ClassName.publishFinish}>
              <label>
                <span>Published link (optional)</span>
                <input value={publishedUrl} onChange={(event) => setPublishedUrl(event.target.value)} placeholder="Paste the final ad link" />
              </label>
              <button type="button" disabled={busyKey === selected._id} onClick={() => void markPublished()}>
                <IoCheckmarkCircleOutline size={19} /> {busyKey === selected._id ? "Saving..." : "Mark published"}
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {showJijiSetup ? (
        <div className={ClassName.modalBackdrop} role="presentation" onMouseDown={() => setShowJijiSetup(false)}>
          <section className={ClassName.setupModal} role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
            <div className={ClassName.modalHeader}>
              <div>
                <span className={ClassName.eyebrow}>ONE-TIME SETUP</span>
                <h2>Enable one-button Jiji posting</h2>
              </div>
              <button type="button" onClick={() => setShowJijiSetup(false)}>Close</button>
            </div>
            <p className={ClassName.setupIntro}>
              The helper uses your signed-in Jiji browser session. BossCar never stores your Jiji password.
            </p>
            <ol className={ClassName.setupSteps}>
              <li>Download and extract the helper folder.</li>
              <li>Open <strong>chrome://extensions</strong> or <strong>edge://extensions</strong>.</li>
              <li>Turn on Developer mode and choose <strong>Load unpacked</strong>.</li>
              <li>Select the extracted folder, then reload this page.</li>
            </ol>
            <a className={ClassName.helperDownload} href="/BossCar-Jiji-Publisher.zip" download>
              <IoCloudDownloadOutline size={19} /> Download Jiji helper
            </a>
            <p className={ClassName.setupNote}>
              Jiji may still pause for CAPTCHA, an unknown required option, or final account review.
            </p>
          </section>
        </div>
      ) : null}
    </div>
  );
}

export default DistributionPage;

const ClassName = {
  page: "min-h-screen min-w-0 flex-1 bg-slate-100 pb-20 text-slate-950",
  header: "flex flex-col justify-between gap-5 border-b border-slate-200 bg-white px-5 pb-6 pt-20 sm:flex-row sm:items-end sm:px-8 xl:pt-8",
  eyebrow: "text-[10px] font-bold tracking-[0.22em] text-primary",
  title: "mt-2 text-2xl font-bold sm:text-3xl",
  subtitle: "mt-2 max-w-2xl text-sm leading-6 text-slate-500",
  refresh: "inline-flex h-10 items-center justify-center gap-2 self-start rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:border-slate-400",
  main: "mx-auto flex w-full max-w-7xl flex-col gap-4 p-4 sm:p-8",
  guide: "flex gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600 [&_strong]:text-slate-950 [&_p]:mt-1 [&_p]:leading-5",
  guideIcon: "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-950 text-white",
  helperReady: "mt-3 inline-flex h-8 items-center rounded-full bg-emerald-50 px-3 text-xs font-semibold text-emerald-700",
  helperSetup: "mt-3 inline-flex h-8 items-center rounded-full bg-slate-950 px-3 text-xs font-semibold text-white hover:bg-slate-800",
  searchRow: "flex h-14 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 focus-within:border-slate-500 [&_input]:min-w-0 [&_input]:flex-1 [&_input]:bg-transparent [&_input]:text-sm [&_input]:outline-none",
  feedback: "rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700",
  empty: "flex min-h-48 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500 [&_strong]:text-slate-900",
  grid: "grid grid-cols-1 gap-4 lg:grid-cols-2",
  card: "overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm",
  pinned: "ring-2 ring-primary ring-offset-2",
  carPreview: "flex w-full gap-4 p-4 hover:bg-slate-50 [&_img]:h-28 [&_img]:w-36 [&_img]:shrink-0 [&_img]:rounded-xl [&_img]:object-cover [&_h2]:mt-2 [&_h2]:truncate [&_h2]:text-base [&_h2]:font-bold [&_strong]:mt-1 [&_strong]:block [&_strong]:text-lg [&_strong]:text-primary [&_p]:mt-1 [&_p]:truncate [&_p]:text-xs [&_p]:text-slate-500",
  badges: "flex flex-wrap gap-1.5 [&_span]:rounded-full [&_span]:bg-slate-100 [&_span]:px-2 [&_span]:py-1 [&_span]:text-[10px] [&_span]:font-semibold [&_span]:text-slate-600",
  platforms: "flex items-center justify-between gap-3 border-t border-slate-100 px-3 py-2",
  checkboxLabel: "relative inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg hover:bg-slate-50",
  checkboxDisabled: "relative inline-flex h-11 w-11 cursor-not-allowed items-center justify-center rounded-lg",
  checkboxBox: "inline-flex h-5 w-5 items-center justify-center rounded-[4px] border transition-colors peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-4 peer-focus-visible:outline-slate-700",
  postButton: "inline-flex h-9 min-w-16 items-center justify-center rounded-lg bg-slate-950 px-4 text-xs font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400",
  postResult: "border-t border-slate-100 bg-blue-50 px-4 py-3 text-xs font-medium text-blue-900",
  statusLine: "flex flex-col gap-1 border-t border-slate-100 px-4 py-3 text-xs text-slate-600",
  modalBackdrop: "fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/55 p-0 sm:items-center sm:p-5",
  modal: "max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl bg-white p-5 shadow-2xl sm:rounded-3xl sm:p-6",
  setupModal: "w-full max-w-lg rounded-t-3xl bg-white p-5 shadow-2xl sm:rounded-3xl sm:p-6",
  setupIntro: "text-sm leading-6 text-slate-600",
  setupSteps: "my-5 list-decimal space-y-3 pl-5 text-sm leading-6 text-slate-700",
  helperDownload: "inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-slate-950 px-4 text-sm font-semibold text-white hover:bg-slate-800",
  setupNote: "mt-3 text-xs leading-5 text-slate-500",
  modalHeader: "mb-5 flex items-start justify-between gap-4 [&_h2]:mt-1 [&_h2]:text-xl [&_h2]:font-bold [&>button]:rounded-lg [&>button]:bg-slate-100 [&>button]:px-3 [&>button]:py-2 [&>button]:text-xs [&>button]:font-semibold",
  copyField: "mb-4 block [&>span]:mb-2 [&>span]:block [&>span]:text-xs [&>span]:font-semibold [&>div]:flex [&>div]:gap-2 [&_input]:min-w-0 [&_input]:flex-1 [&_input]:rounded-xl [&_input]:border [&_input]:border-slate-200 [&_input]:px-3 [&_input]:text-sm [&_textarea]:w-full [&_textarea]:resize-y [&_textarea]:rounded-xl [&_textarea]:border [&_textarea]:border-slate-200 [&_textarea]:p-3 [&_textarea]:text-sm [&_textarea]:leading-6 [&_button]:inline-flex [&_button]:items-center [&_button]:justify-center [&_button]:gap-1 [&_button]:rounded-xl [&_button]:bg-slate-100 [&_button]:px-3 [&_button]:text-xs [&_button]:font-semibold",
  copyButton: "mt-2 h-10 w-full",
  fieldLabel: "mb-2 block text-xs font-semibold",
  photoHeader: "flex items-center justify-between gap-3 [&>button]:mb-2 [&>button]:inline-flex [&>button]:items-center [&>button]:gap-1 [&>button]:text-xs [&>button]:font-semibold [&>button]:text-slate-600 hover:[&>button]:text-slate-950",
  photos: "grid grid-cols-4 gap-2 sm:grid-cols-6 [&_a]:relative [&_a]:overflow-hidden [&_a]:rounded-lg [&_a]:bg-slate-100 [&_img]:aspect-square [&_img]:h-full [&_img]:w-full [&_img]:object-cover [&_span]:absolute [&_span]:bottom-1 [&_span]:right-1 [&_span]:rounded [&_span]:bg-black/70 [&_span]:px-1.5 [&_span]:py-0.5 [&_span]:text-[9px] [&_span]:text-white",
  openTarget: "mt-4 inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-slate-950 text-sm font-semibold text-white",
  publishFinish: "mt-4 flex flex-col gap-3 rounded-2xl bg-slate-100 p-4 sm:flex-row sm:items-end [&_label]:min-w-0 [&_label]:flex-1 [&_label>span]:mb-2 [&_label>span]:block [&_label>span]:text-xs [&_label>span]:font-semibold [&_input]:h-11 [&_input]:w-full [&_input]:rounded-xl [&_input]:border [&_input]:border-slate-200 [&_input]:bg-white [&_input]:px-3 [&_input]:text-sm [&>button]:inline-flex [&>button]:h-11 [&>button]:items-center [&>button]:justify-center [&>button]:gap-2 [&>button]:rounded-xl [&>button]:bg-primary [&>button]:px-5 [&>button]:text-sm [&>button]:font-semibold [&>button]:text-white [&>button:disabled]:opacity-50",
};
